import { FC, useState, useMemo, useCallback, useEffect } from "react";

import Select from "react-select";

import { CustomInput } from "../../kits/inputs/Input";
import CustomButton from "../../kits/custom-button/CustomButton";
import useAxios from "../../../services/useAxios";

import BannerCard from "./BannerCard";
// import ImagePicker from "./ImagePicker";

import {
  SelectOptions,
  BannerData,
  BannerInputData,
  GetBannerResponse,
  FindBannerManagementResponse,
  AddBannerRequest,
  AddBannerResponse,
  UpdateBannerManagementRequest,
  UpdateBannerManagementResponse,
  FindBannerManagementRequest,
} from "./BaMaTypes";
import { BannerOrders, BannerTypes } from "./BaMaData";

import "./banner-management.scss";
import Loader from "../utils/Loader";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";

const BannerManagement: FC = () => {
  // api connections
  const {
    execute: GetBannerAPI,
    firstLoading: GetBannerFirstLoading,
    loading: GetBannerLoading,
  } = useAxios("api/get_banner");

  const {
    execute: FindBannerManagementAPI,
    firstLoading: FindBannerManagementFirstLoading,
    loading: FindBannerManagementLoading,
  } = useAxios("api/Find_Banner_management");

  const { execute: AddBannerAPI, loading: AddBannerLoading } =
    useAxios("api/add_banner");

  const {
    execute: UpdateBannerManagementAPI,
    loading: UpdateBannerManagementLoading,
  } = useAxios("api/Update_Banner_management");

  // selected input state
  const [inputs, setInputs] = useState<BannerInputData>({
    place: "-1",
    type: "-1",
    order: "-1",
    link: "",
    image: null,
  });

  // input list states
  const [bannerPlaces, setBannerPlaces] = useState<SelectOptions>([]);

  //  banner list state
  const [mainBannerList, setMainBannerList] = useState<BannerData[]>([]);

  // generate banner list depending on bannerPlaces, bannerTypes and bannerOrders
  const bannerList = useMemo(
    () =>
      mainBannerList.map((banner) => {
        let place =
          bannerPlaces.find((place) => "" + place.value === "" + banner.place)
            ?.label ?? banner.place;

        let type =
          BannerTypes.find((type) => +type.value === +banner.type)?.label ??
          banner.type;

        let order =
          BannerOrders.find((order) => +order.value === +banner.order)?.label ??
          banner.order;

        let link = banner.link || "---";

        return {
          ...banner,
          place,
          type,
          order,
          link,
        };
      }),
    [mainBannerList, bannerPlaces]
  );

  //  Select inputs' values
  const bannerPlace = useMemo(
    () => bannerPlaces.filter((b) => b.value === inputs.place),
    [bannerPlaces, inputs.place]
  );
  const bannerType = useMemo(
    () => BannerTypes.filter((b) => +b.value === +inputs.type),
    [inputs.type]
  );
  const bannerOrder = useMemo(
    () => BannerOrders.filter((b) => +b.value === +inputs.order),
    [inputs.order]
  );

  // Functions
  const getBannerLists = useCallback(() => {
    const data: FindBannerManagementRequest = {
      conditions: "status/=/0",
      tbl_name: "LEFT JOIN,files,fileId,id",
    };

    FindBannerManagementAPI(data).then(
      (response: FindBannerManagementResponse) => {
        setMainBannerList(
          response
            .sort((a, b) => +b.mtm - +a.mtm)
            .map((banner) => ({
              id: +banner.banner_management__id,
              place: banner.position,
              type: banner.type,
              order: banner.sort,
              link: banner.url,
              image: banner.path,
            }))
        );
      }
    );
  }, []);

  // Event Handlers
  const linkHandler = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => setInputs((prev) => ({ ...prev, link: event.target.value })),
    []
  );

  const imageHandler = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setInputs((prev) => ({
        ...prev,
        image:
          event.target.files !== null &&
          typeof event.target.files[0] !== "undefined"
            ? event.target.files[0]
            : null,
      }));
      // const file = document.getElementById("imageSelect");
      // console.log(file?.getAttribute("value"));
    },
    []
  );

  const deleteFunction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, banner: BannerData) => {
      event.preventDefault();

      const data: UpdateBannerManagementRequest = {
        conditions: `id/=/${banner.id}`,
        keyvalues: "status,-1",
      };

      UpdateBannerManagementAPI(data).then(
        (response: UpdateBannerManagementResponse) => {
          if (+response.status === 1) {
            toast.success("بنر با موفقیت حذف شد.");
            getBannerLists();
          } else {
            Swal.fire({
              title: "خطا!",
              text: "مشکلی در حذف بنر بوجود آمده است. لطفا مجددا تلاش کنید.",
              confirmButtonText: "متوجه شدم",
              icon: "error",
            });
          }
        }
      );
    },
    []
  );

  const onSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      const data: AddBannerRequest = {
        position: inputs.place,
        type: inputs.type,
        sort: inputs.order,
        url: inputs.link,
        file: inputs.image !== null ? inputs.image : "",
      };
      if (
        inputs?.place == "-1" ||
        inputs?.type == "-1" ||
        inputs?.order == "-1" ||
        inputs?.image == null
      ) {
        toast.error("لطفا مقادیر جایگاه، نوع، ترتیب و عکس را وارد کنید.");
      } else {
        AddBannerAPI(data).then((response: AddBannerResponse) => {
          if (+response.status === 1) {
            toast.success("بنر با موفقیت ثبت شد.");
            setInputs({
              place: "-1",
              type: "-1",
              order: "-1",
              link: "",
              image: null,
            });
            getBannerLists();
          } else {
            Swal.fire({
              title: "خطا!",
              text: "مشکلی در ثبت بنر بوجود آمده است. لطفا مجددا تلاش کنید.",
              confirmButtonText: "متوجه شدم",
              icon: "error",
            });
          }
        });
      }
    },
    [inputs, AddBannerAPI, getBannerLists]
  );

  useEffect(() => {
    GetBannerAPI().then((response: GetBannerResponse) => {
      setBannerPlaces(
        response.bannerItem.map((place) => ({
          label: place.title,
          value: place.id,
        }))
      );
      getBannerLists();
    });
  }, []);

  return (
    <main className="banner-management pd-32">
      {(!GetBannerFirstLoading || GetBannerLoading) && <Loader />}
      <h1 className="title-h2-m mr-b-20">مدیریت بنرها</h1>
      {/* add banner */}
      <section className="add-banner d-flex justify-content-center flex-column pd-x-24 pd-t-40 pd-b-32 mr-r-32 mr-l-20">
        <h4 className="add-banner-title title-h3-m">افزودن بنر</h4>
        <form
          className="w-100 row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 align-items-center mr-r-12"
          onSubmit={onSubmit}
        >
          <div className="col mr-b-12">
            <label className="label-style desc-m">جایگاه بنر</label>
            <Select
              classNamePrefix={`banner-inputs`}
              options={bannerPlaces}
              value={bannerPlace}
              onChange={(event) => {
                if (typeof event?.value !== "undefined") {
                  setInputs((prev) => ({ ...prev, place: event.value }));
                }
              }}
            />
          </div>
          <div className="col mr-b-12">
            <label className="label-style desc-m">نوع بنر</label>
            <Select
              classNamePrefix={`banner-inputs`}
              options={BannerTypes}
              value={bannerType}
              onChange={(event) => {
                if (typeof event?.value !== "undefined") {
                  setInputs((prev) => ({ ...prev, type: event.value }));
                }
              }}
            />
          </div>
          <div className="col mr-b-12">
            <label className="label-style desc-m">ترتیب</label>
            <Select
              classNamePrefix={`banner-inputs`}
              options={BannerOrders}
              value={bannerOrder}
              onChange={(event) => {
                if (typeof event?.value !== "undefined") {
                  setInputs((prev) => ({ ...prev, order: event.value }));
                }
              }}
            />
          </div>
          <div className="col mr-b-12">
            <CustomInput
              title="لینک"
              value={inputs.link}
              onChange={linkHandler}
            />
          </div>
          <div className="col mr-b-20" style={{ overflow: "hidden" }}>
            <label className="label-style desc-m">انتخاب عکس</label>
            <input type="file" onChange={imageHandler} id="imageSelect" />
          </div>
          <div className="col d-flex justify-content-center">
            <CustomButton
              state={!!AddBannerLoading ? "loading" : "default"}
              style={{ width: "200px" }}
            >
              ثبت
            </CustomButton>
          </div>
        </form>
      </section>
      <section className="d-flex flex-column align-items-center justify-content-center">
        {(!FindBannerManagementFirstLoading ||
          FindBannerManagementLoading ||
          UpdateBannerManagementLoading) && <Loader />}
        {bannerList.map((banner, index) => (
          <BannerCard
            banner={banner}
            index={index}
            deleteFunction={(event) => deleteFunction(event, banner)}
          />
        ))}
      </section>
    </main>
  );
};

export default BannerManagement;
